import React, { FunctionComponent, ReactNode, useState } from 'react'

import { useField } from 'formik'
import { NumberFormatValues, NumericFormatProps } from 'react-number-format'
import { SpaceProps, WidthProps } from 'styled-system'

import { NumericInput } from '@/atoms/input'
import { isNil } from '@/helpers/type-guards'
import { AdornmentPosition, Currency } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: string
  name?: string
  fieldProps?: SpaceProps & WidthProps
  onChange?: (val: NumberFormatValues) => void
  onBlur?: () => void
  adornment?: ReactNode
  adornmentPosition?: AdornmentPosition
  isDisabled?: boolean
  hideErrorMessage?: boolean
  currency?: Currency
  tooltip?: string
}

export type NumericInputProps = Omit<OwnProps & NumericFormatProps, 'onChange'> & {
  onChange?: (val: NumberFormatValues) => void
}

const NumericInputField: FunctionComponent<NumericInputProps> = ({
  label,
  currency,
  fieldProps,
  name,
  onChange,
  hideErrorMessage,
  onBlur,
  tooltip,
  fieldValue: _fieldValue,
  valueFormat: _valueFormat,
  ...rest
}: NumericInputProps & { fieldValue?: unknown; valueFormat?: unknown }) => {
  const [field, meta, helpers] = useField(name)
  const [floatValue, setFloatValue] = useState<number>()
  const hasError: boolean = meta.touched && !!meta.error

  const handleInputChange = (values: NumberFormatValues) => {
    const floatValue = isNil(values?.floatValue) ? '' : values.floatValue
    helpers && helpers.setValue(floatValue)
    setFloatValue(values.floatValue)

    onChange && onChange(values)
  }
  const handleInputBlur = (): void => {
    helpers && helpers.setTouched(true)
    onBlur && onBlur()
  }

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
      <NumericInput
        id={field.name}
        hasError={hasError}
        name={field.name}
        value={field.value}
        onValueChange={handleInputChange}
        onBlur={handleInputBlur}
        floatvalue={floatValue}
        {...rest}
      />
    </FieldWrapper>
  )
}

NumericInputField.displayName = 'NumericInputField'

export default NumericInputField
